
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'sonner';
import { dbService } from '../db';
import { ExamResult, Student } from '../db/types';

// Helper function to clean score values
const cleanScoreValue = (value: any): number => {
  if (value === undefined || value === null) return 0;
  
  if (typeof value === 'string') {
    // Remove % sign if present and convert to number
    return parseFloat(value.replace('%', '')) || 0;
  }
  return value || 0;
};

export async function getStudentExams(studentId: number | string): Promise<ExamResult[]> {
  try {
    const numericId = typeof studentId === 'string' ? parseInt(studentId, 10) : studentId;
    const student = await dbService.getStudent(numericId);
    
    if (!student) {
      throw new Error(`Student with ID ${studentId} not found`);
    }
    
    return student.examResults || [];
  } catch (error) {
    console.error('Error fetching student exams:', error);
    toast.error('Failed to load exam results');
    return [];
  }
}

export async function addExamResult(
  studentId: number | string, 
  examData: Omit<ExamResult, 'id' | 'createdAt'>
): Promise<boolean> {
  try {
    const numericId = typeof studentId === 'string' ? parseInt(studentId, 10) : studentId;
    const student = await dbService.getStudent(numericId);
    
    if (!student) {
      throw new Error(`Student with ID ${studentId} not found`);
    }
    
    // Clean up score values to ensure they are numbers
    const cleanedExamData = {
      ...examData,
      obtainedScore: cleanScoreValue(examData.obtainedScore),
      maxScore: cleanScoreValue(examData.maxScore)
    };
    
    const newExam: ExamResult = {
      ...cleanedExamData,
      id: uuidv4(),
      createdAt: new Date().toISOString()
    };
    
    const updatedStudent: Student = {
      ...student,
      examResults: [...(student.examResults || []), newExam],
      updatedAt: new Date().toISOString()
    };
    
    await dbService.updateStudent(numericId, updatedStudent);
    toast.success('Exam result added successfully');
    return true;
  } catch (error) {
    console.error('Error adding exam result:', error);
    toast.error('Failed to add exam result');
    return false;
  }
}

export async function updateExamResult(
  studentId: number | string,
  examId: string,
  examData: Partial<Omit<ExamResult, 'id' | 'createdAt'>>
): Promise<boolean> {
  try {
    const numericId = typeof studentId === 'string' ? parseInt(studentId, 10) : studentId;
    const student = await dbService.getStudent(numericId);
    
    if (!student || !student.examResults) {
      throw new Error(`Student with ID ${studentId} not found or has no exam results`);
    }
    
    const examIndex = student.examResults.findIndex(exam => exam.id === examId);
    
    if (examIndex === -1) {
      throw new Error(`Exam with ID ${examId} not found for student ${studentId}`);
    }
    
    // Clean up score values if they exist in the update data
    const cleanedExamData = { ...examData };
    if (examData.obtainedScore !== undefined) {
      cleanedExamData.obtainedScore = cleanScoreValue(examData.obtainedScore);
    }
    if (examData.maxScore !== undefined) {
      cleanedExamData.maxScore = cleanScoreValue(examData.maxScore);
    }
    
    const updatedExams = [...student.examResults];
    updatedExams[examIndex] = {
      ...updatedExams[examIndex],
      ...cleanedExamData
    };
    
    const updatedStudent: Student = {
      ...student,
      examResults: updatedExams,
      updatedAt: new Date().toISOString()
    };
    
    await dbService.updateStudent(numericId, updatedStudent);
    toast.success('Exam result updated successfully');
    return true;
  } catch (error) {
    console.error('Error updating exam result:', error);
    toast.error('Failed to update exam result');
    return false;
  }
}

export async function deleteExamResult(
  studentId: number | string,
  examId: string
): Promise<boolean> {
  try {
    const numericId = typeof studentId === 'string' ? parseInt(studentId, 10) : studentId;
    const student = await dbService.getStudent(numericId);
    
    if (!student || !student.examResults) {
      throw new Error(`Student with ID ${studentId} not found or has no exam results`);
    }
    
    const filteredExams = student.examResults.filter(exam => exam.id !== examId);
    
    if (filteredExams.length === student.examResults.length) {
      throw new Error(`Exam with ID ${examId} not found for student ${studentId}`);
    }
    
    const updatedStudent: Student = {
      ...student,
      examResults: filteredExams,
      updatedAt: new Date().toISOString()
    };
    
    await dbService.updateStudent(numericId, updatedStudent);
    toast.success('Exam result deleted successfully');
    return true;
  } catch (error) {
    console.error('Error deleting exam result:', error);
    toast.error('Failed to delete exam result');
    return false;
  }
}
